import { api, descargarArchivo, getActiveTenantHeader, subirArchivo } from "@/lib/api"

/**
 * EL EXPEDIENTE DE LA EMPRESA.
 *
 *   GET    /expediente/tipos
 *   GET    /expediente/documentos        ?entity_id&incluir_inactivos
 *   POST   /expediente/documentos        (multipart)
 *   DELETE /expediente/documentos/{id}   ?motivo
 *   GET    /expediente/declaraciones     ?entity_id&anio
 *   POST   /expediente/declaraciones     (multipart)
 *   GET    /expediente/descargar/{clase}/{id}
 *
 * SIN MODO MOCK, igual que la carga por archivo: esto ESCRIBE, y un mock que
 * responde «archivado» sin archivar nada es el defecto que este producto no se
 * puede permitir — aquí además el archivo es la evidencia de algo presentado.
 */

function exigirTenantActivo(): void {
  if (!getActiveTenantHeader()) {
    throw new Error(
      "No hay cliente activo. Elige uno en la barra superior antes de subir nada.",
    )
  }
}

export interface TipoDeDocumento {
  id: string
  nombre: string
}

export function fetchTiposDeDocumento(): Promise<{ tipos: TipoDeDocumento[] }> {
  return api.get("/expediente/tipos")
}

export interface DocumentoEmpresa {
  id: string
  entity_id: string | null
  /** «Todas las sociedades» cuando no cuelga de ninguna. Lo resuelve el backend. */
  alcance: string
  tipo: string
  tipo_nombre: string
  nombre: string
  vigente_hasta: string | null
  /** Calculado contra la fecha de hoy EN EL BACKEND, para que sea uno solo. */
  vencido: boolean
  nombre_archivo: string
  mime: string | null
  bytes: number | null
  nota: string | null
  activo: boolean
  creado_en: string
}

export function fetchDocumentos(
  entityId?: string | null, incluirInactivos = false,
): Promise<{ documentos: DocumentoEmpresa[]; vencidos: number; nota: string }> {
  return api.get("/expediente/documentos", {
    query: {
      entity_id: entityId || undefined,
      incluir_inactivos: incluirInactivos ? "true" : undefined,
    },
  })
}

export function subirDocumento(datos: {
  archivo: File
  tipo: string
  nombre: string
  entityId?: string | null
  vigenteHasta?: string | null
  nota?: string | null
}): Promise<{ id: string; nombre: string }> {
  const form = new FormData()
  form.append("archivo", datos.archivo)
  form.append("tipo", datos.tipo)
  form.append("nombre", datos.nombre)
  if (datos.entityId) form.append("entity_id", datos.entityId)
  if (datos.vigenteHasta) form.append("vigente_hasta", datos.vigenteHasta)
  if (datos.nota) form.append("nota", datos.nota)
  form.append("motivo", "Documento de la empresa archivado desde el expediente.")
  exigirTenantActivo()
  return subirArchivo("/expediente/documentos", form)
}

export function darDeBajaDocumento(id: string, motivo: string) {
  return api.delete<{ id: string; activo: boolean }>(
    `/expediente/documentos/${encodeURIComponent(id)}`, { query: { motivo } })
}

export interface DeclaracionPresentada {
  id: string
  entity_id: string
  razon_social: string
  pais: string
  anio: number
  mes: number | null
  codigo: string
  nombre: string | null
  presentada_en: string
  numero_orden: string | null
  importe_pagado: number | null
  moneda: string | null
  nombre_archivo: string
  bytes: number | null
  nota: string | null
}

export function fetchDeclaracionesPresentadas(
  entityId?: string | null, anio?: number,
): Promise<{ declaraciones: DeclaracionPresentada[]; nota: string }> {
  return api.get("/expediente/declaraciones", {
    query: { entity_id: entityId || undefined, anio },
  })
}

export function archivarDeclaracion(datos: {
  archivo: File
  entityId: string
  anio: number
  mes?: number | null
  codigo: string
  nombre?: string | null
  presentadaEn: string
  numeroOrden?: string | null
  importePagado?: number | null
  moneda?: string | null
}): Promise<{ id: string; codigo: string; siguiente: string }> {
  const form = new FormData()
  form.append("archivo", datos.archivo)
  form.append("anio", String(datos.anio))
  form.append("codigo", datos.codigo)
  form.append("presentada_en", datos.presentadaEn)
  if (datos.mes) form.append("mes", String(datos.mes))
  if (datos.nombre) form.append("nombre", datos.nombre)
  if (datos.numeroOrden) form.append("numero_orden", datos.numeroOrden)
  if (datos.importePagado != null) form.append("importe_pagado", String(datos.importePagado))
  if (datos.moneda) form.append("moneda", datos.moneda)
  form.append("motivo", "Declaración presentada archivada.")
  exigirTenantActivo()
  return subirArchivo(
    `/expediente/declaraciones?entity_id=${encodeURIComponent(datos.entityId)}`, form)
}

/**
 * Descarga el archivo. `clase` es `declaracion` o `documento`.
 *
 * Pasa por `descargarArchivo` y no por un `<a href>`: la descarga necesita el
 * token y la cabecera de cliente, que un enlace suelto no lleva — saldría un 401
 * que el navegador enseña como una página en blanco.
 */
export function descargarDelExpediente(
  clase: "declaracion" | "documento", id: string, nombre: string,
): Promise<void> {
  return descargarArchivo(
    `/expediente/descargar/${clase}/${encodeURIComponent(id)}`, nombre)
}
